<script setup lang="ts">
import { toRef, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LocaleCode } from '../../../utils/i18n'
import MultiSelect from '@vueform/multiselect'
import _ from 'lodash'
import { get, put } from '@rails/request.js'

type Franchise = {
  id: number | null,
  name: string
}

type Option = {
  value: Franchise,
  label: string,
}
const props = defineProps<{
  locale: LocaleCode,
  data: {
    franchises: Franchise[],
    paths: {
      bulk_update_path: string,
      game_franchises_path: string,
      franchise_autocomplete_path: string
    }
  }
}>()

const { t } = useI18n({ locale: props.locale })
const gameFranchises = toRef(props.data, 'franchises')

const initialValue = computed(() => {
  return gameFranchises.value.map(gf =>(
    { value: gf, label: gf.name }
  ))
})

const selectedFranchises = ref(initialValue.value)

const searchOptions = async(url: string, query: string): Promise<Option[] | null> => {
  const response = await get(url, {
    responseKind: "json",
    query: { name: query }
  })
  if(response.ok) {
    const json = await response.json
    return json.franchises.map((sg: Franchise) => {
      return { value: sg, label: sg.name }
    })
  }
  return null;
}

const onSubmit = async() => {
  const response = await put(props.data.paths.bulk_update_path, {
    responseKind: "json",
    body: {
      game_franchises: {
        franchises: selectedFranchises.value.map(gf => (gf.value))
      }
    }
  })
  if(response.ok) {
    await get(props.data.paths.game_franchises_path, {
      responseKind: "turbo-stream"
    })
  } else {
    alert(t('error_occured'))
  }
  return null;
}

const autocomplete = async (query: string) => {
  return await searchOptions(props.data.paths.franchise_autocomplete_path, query);
}

const selectFranchise = (e: { originalEvent: Event, value: Franchise | string }) => {
  let option: Option
  if (typeof e.value == 'string') {
    option = {
      label: e.value,
      value: {
        id: null,
        name: e.value
      }
    }
  } else {
    option = {
      label: e.value.name,
      value: e.value
    }
  }
  selectedFranchises.value.push(option)
}

const onClearFranchise = () => {
  selectedFranchises.value = []
}

const deselectFranchise = (e: { originalEvent: Event, value: Franchise | string }) => {
  selectedFranchises.value = selectedFranchises.value.filter((option: Option) => {
    if (typeof e.value == 'string') {
      return option.value.id || option.label != e.value
    } else {
      return option.value.id != e.value.id
    }
  })
}

</script>

<template lang="pug">
form
  div.modal__body(class="uk-modal-body" uk-overflow-auto)
    div.games__modal-franchise
      div.games__modal-franchise-description
        p.games__modal-franchise-text(v-html="t('description_1')")
        div.games__modal-franchise-value
          MultiSelect(
            :value="initialValue"
            @select="selectFranchise"
            @deselect="deselectFranchise"
            @clear="onClearFranchise"
            :options="autocomplete"
            mode="tags"
            class="uk-input uk-border-pill"
            :placeholder="t('placeholder')"
            :searchable="true"
            :createOption="true"
            :filter-results="false"
            :resolve-on-load="false"
            :object="true"
            track-by="label"
            :delay="300"
            :noResultsText="t('no_results')"
            :noOptionsText="t('no_options')"
            id="game_franchises_franchise_ids"
          )
        p.games__modal-franchise-caption {{ t('description_2') }}
        p.games__modal-franchise-text(v-html="t('description_3')")
      div.games__modal-franchise-expander.closed(id="franchise_guidance")
        div.games__modal-franchise-guidance
          p.games__modal-franchise-text(v-html="t('guidance_1')")
          p.games__modal-franchise-text(v-html="t('guidance_2')") 
          p.games__modal-franchise-text(v-html="t('guidance_3')") 
          p.games__modal-franchise-text(v-html="t('guidance_4')")
        div.games__modal-franchise-button(uk-toggle="target: #franchise_guidance; cls: closed")
          div.games__modal-franchise-button-open {{ t('guidance_button.open') }}
          div.games__modal-franchise-button-close {{ t('guidance_button.close') }}

  div.modal__footer(class="uk-modal-footer uk-text-center")
    button(class="uk-button uk-button-default uk-modal-close" type="button")
      | {{ t('close') }}
    button(
      class="uk-button uk-button-primary uk-modal-submit"
      type="button"
      @click="onSubmit"
    )
      | {{ t('save') }}
</template>

<i18n lang="json">{
  "ja": {
    "description_1": "このゲームを、指定のフランチャイズに加えることができます。<br>フランチャイズ名は、一般的にスピンオフ～派生作品のルーツとなった「メインキャラクター名」「初代作品名」で簡略化されて呼ばれています。",
    "description_2": "上記にフランチャイズ（キャラクター名やブランド名）を入力し、リストが出てきた場合は該当するものを選択してください。",
    "description_3": "リストが表示されない・該当するものがない場合は、入力後にエンターを押してください。新しい「フランチャイズ」タグが生成されます。<br>登録のガイドラインおよび登録例については以下の説明をお読みください。",
    "guidance_button": {
      "open": "フランチャイズ登録について",
      "close": "ガイドラインを閉じる"
    },
    "guidance_1": "「シリーズ」は直系の後継作を繋げるものですが、「フランチャイズ」はいわゆる姉妹作（スピンオフ作品や派生作品）も含めて表現するためのブランド名です。<br>フランチャイズ名は性質上、スピンオフ作品等の誕生によって初めて簡略表現が行われるようになり、徐々に一般表現化していくものです。以下例のように、一般的にそれぞれの派生元となったゲームにおける「キャラクター名」「ゲームタイトルの一部分（1～2単語）」で呼ばれています。",
    "guidance_2": "<span class='tag'>マリオ</span><br>これは、マリオパーティ、ルイージマンション、マリオメーカー、ワリオの森など、「'マリオの世界観'を継承しているゲーム」が登録するフランチャイズ名です。",
    "guidance_3": "<span class='tag'>ゼルダ</span><br>これは、リンクの冒険、ゼルダ無双、ゼルダの伝説：ブレスオブザワイルドなど、「'ゼルダの伝説（≒リンク）の世界観'を継承しているゲーム」が登録するフランチャイズ名です。",
    "guidance_4": "好きなキャラクターや個性的な世界観で、全く別のゲーム体験ができるという情報は、とても価値があると考えています。<br>もし未登録のフランチャイズ名があれば、登録のご協力をお願い致します。",
    "placeholder": "複数登録できます",
    "save": "保存",
    "close": "閉じる",
    "no_results": "フランチャイズが見つかりませんでした",
    "no_options": "フランチャイズ名を入力してください",
    "error_occured": "エラーが発生しました"
  }
}</i18n>

<style src="@vueform/multiselect/themes/default.css"></style>
